import { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarDays, CreditCard, Plus, ReceiptText, WalletCards } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../components/ui/sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const defaultPaymentMethodOptions = [
  { value: 'Cash', label: 'Bayar Langsung' },
  { value: 'Bank Transfer', label: 'Transfer' },
  { value: 'QRIS', label: 'QRIS' },
];
const paymentMethodLabelMap = defaultPaymentMethodOptions.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const formatRupiah = (amount) => `Rp ${Math.round(Number(amount || 0)).toLocaleString('id-ID')}`;
const getPaymentCategoryBadge = (description = '') => {
  const text = String(description || '').toLowerCase();
  if (text.includes('pt package') || text.includes('paket pt') || text.includes(' bfp pt')) {
    return { label: 'PT', className: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300' };
  }
  if (text.includes('cardio') || text.includes('coach')) {
    return { label: 'Cardio', className: 'border-sky-500/30 bg-sky-500/10 text-sky-300' };
  }
  if (text.includes('all access')) {
    return { label: 'All Access', className: 'border-[#D4AF37]/35 bg-[#D4AF37]/10 text-[#F3D77A]' };
  }
  if (text.includes('gym') || text.includes('membership')) {
    return { label: 'Gym', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' };
  }
  return { label: 'Lainnya', className: 'border-white/10 bg-white/5 text-zinc-300' };
};
const getPaymentStatusBadgeClass = (status = '') => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'cancelled' || normalized === 'canceled') {
    return 'border-red-500/25 bg-red-500/10 text-red-300';
  }
  if (normalized === 'refunded' || normalized === 'failed') {
    return 'border-zinc-500/25 bg-zinc-500/10 text-zinc-300';
  }
  return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-300';
};
const formatPlanIntervalUnit = (unit, { pt = false } = {}) => {
  const normalizedUnit = String(unit || 'MONTH').toUpperCase();
  if (pt && normalizedUnit === 'DAY') return 'sesi';
  if (normalizedUnit === 'DAY') return 'Hari';
  if (normalizedUnit === 'WEEK') return 'Minggu';
  return 'Bulan';
};
const normalizeCoachText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const normalizeClassText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const isCoachNameMatch = (selectedCoachName, candidateCoachName) => {
  const selected = normalizeCoachText(selectedCoachName);
  const candidate = normalizeCoachText(candidateCoachName);
  if (!selected || !candidate) return false;
  return selected === candidate || selected.includes(candidate) || candidate.includes(selected);
};
const extractDateValue = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};
const pickAutoClassDate = (dateOptions) => {
  if (!Array.isArray(dateOptions) || dateOptions.length === 0) return '';
  const today = new Date().toISOString().slice(0, 10);
  const nextUpcoming = dateOptions.find((dateValue) => String(dateValue) >= today);
  return nextUpcoming || dateOptions[dateOptions.length - 1] || '';
};
const isPastClassDate = (value) => {
  const dateValue = extractDateValue(value);
  if (!dateValue) return true;
  const today = new Date().toISOString().slice(0, 10);
  return String(dateValue) < today;
};
const isManualCoachClassSlot = (item) =>
  normalizeClassText(item?.source) === 'admin_manual_coach'
  && normalizeClassText(item?.full_name) === 'kelas umum';
const isClosedManualCoachClassSlot = (item) => {
  const statusValue = normalizeClassText(item?.attendance_status);
  const bookingStatus = normalizeClassText(item?.status);
  return (
    item?.attendance_status_selected === true
    || ['done', 'not_done', 'cancelled', 'canceled', 'cancel'].includes(statusValue)
    || ['done', 'completed', 'cancelled', 'canceled', 'cancel'].includes(bookingStatus)
  );
};
const isOpenUpcomingManualCoachClassSlot = (item) => {
  if (!isManualCoachClassSlot(item) || isClosedManualCoachClassSlot(item)) return false;
  return !isPastClassDate(item?.class_date || item?.created_at);
};

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState(defaultPaymentMethodOptions);
  const [ptTrainers, setPtTrainers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [classBookings, setClassBookings] = useState([]);
  const [cardioCoaches, setCardioCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [selectedPaymentCategory, setSelectedPaymentCategory] = useState('');
  const [formData, setFormData] = useState({
    member_id: '',
    visitor_phone: '',
    visitor_address: '',
    amount: '',
    description: '',
    payment_type: '',
    payment_method: 'Cash',
    preferred_start_date: new Date().toISOString().slice(0, 10),
  });
  const [ptSelection, setPtSelection] = useState({ ptName: '' });
  const [cardioSelection, setCardioSelection] = useState({ coachName: '', classDate: '' });
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateNames, setDuplicateNames] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const membershipPaymentTypeOptions = useMemo(() => {
    return (Array.isArray(plans) ? plans : [])
      .filter((plan) => {
        if (!plan || plan.is_active === false) return false;
        return String(plan.category || 'GYM').trim().toUpperCase() === 'GYM';
      })
      .sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return Number(a.price || 0) - Number(b.price || 0);
      })
      .map((plan) => {
        const intervalValue = Number(plan.interval_value || 1);
        const intervalUnit = formatPlanIntervalUnit(plan.interval_unit);
        return {
          value: `plan:${plan.id}`,
          label: `${plan.name} (${intervalValue} ${intervalUnit})`,
          price: Number(plan.price || 0),
          description: `Gym ${plan.name}`,
        };
      });
  }, [plans]);

  const cardioPaymentTypeOptions = useMemo(() => {
    return (Array.isArray(plans) ? plans : [])
      .filter((plan) => {
        if (!plan || plan.is_active === false) return false;
        return String(plan.category || '').trim().toUpperCase() === 'CARDIO';
      })
      .sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return Number(a.price || 0) - Number(b.price || 0);
      })
      .map((plan) => {
        const intervalValue = Number(plan.interval_value || 1);
        const intervalUnit = formatPlanIntervalUnit(plan.interval_unit);
        return {
          value: `cardio:${plan.id}`,
          label: `${plan.name} (${intervalValue} ${intervalUnit})`,
          price: Number(plan.price || 0),
          description: `Cardio ${plan.name}`,
          package_name: String(plan.name || '').trim(),
          coach_name: String(plan.coach_name || '').trim(),
          coach_id: String(plan.coach_id || '').trim(),
          interval_value: intervalValue,
          interval_unit: String(plan.interval_unit || 'MONTH').toUpperCase(),
        };
      });
  }, [plans]);

  const ptPaymentTypeOptions = useMemo(() => {
    return (Array.isArray(plans) ? plans : [])
      .filter((plan) => {
        if (!plan || plan.is_active === false) return false;
        return String(plan.category || '').trim().toUpperCase() === 'PT';
      })
      .sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        const sessionA = Number(a.interval_value || 0);
        const sessionB = Number(b.interval_value || 0);
        if (sessionA !== sessionB) return sessionA - sessionB;
        return Number(a.price || 0) - Number(b.price || 0);
      })
      .map((plan) => {
        const intervalValue = Number(plan.interval_value || 1);
        const intervalUnit = formatPlanIntervalUnit(plan.interval_unit || 'DAY', { pt: true }).toLowerCase();
        return {
          value: `pt:${plan.id}`,
          label: `${plan.name} (${intervalValue} ${intervalUnit})`,
          price: Number(plan.price || 0),
          description: `PT Package ${plan.name} (${intervalValue} ${intervalUnit})`,
          package_name: String(plan.name || '').trim(),
          sessions: intervalValue,
          interval_unit: String(plan.interval_unit || 'DAY').toUpperCase(),
        };
      });
  }, [plans]);

  const allAccessPaymentTypeOptions = useMemo(() => {
    return (Array.isArray(plans) ? plans : [])
      .filter((plan) => {
        if (!plan || plan.is_active === false) return false;
        return String(plan.category || '').trim().toUpperCase() === 'ALL_ACCESS';
      })
      .sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return Number(a.price || 0) - Number(b.price || 0);
      })
      .map((plan) => {
        const intervalValue = Number(plan.interval_value || 1);
        const intervalUnit = formatPlanIntervalUnit(plan.interval_unit);
        return {
          value: `all_access:${plan.id}`,
          label: `${plan.name} (${intervalValue} ${intervalUnit})`,
          price: Number(plan.price || 0),
          description: `All Access ${plan.name}`,
          package_name: String(plan.name || '').trim(),
        };
      });
  }, [plans]);

  const ptNameOptions = useMemo(
    () =>
      (Array.isArray(ptTrainers) ? ptTrainers : [])
        .filter((item) => item && item.is_active !== false)
        .sort((a, b) => {
          const orderA = Number(a.sort_order || 0);
          const orderB = Number(b.sort_order || 0);
          if (orderA !== orderB) return orderA - orderB;
          return String(a.name || '').localeCompare(String(b.name || ''));
        })
        .map((item) => String(item.name || '').trim())
        .filter(Boolean),
    [ptTrainers]
  );

  const cardioCoachAvailabilityMap = useMemo(() => {
    const dayMap = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 0,
    };
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const map = new Map();
    (Array.isArray(schedules) ? schedules : []).forEach((item) => {
      const coachName = String(item?.instructor || '').trim();
      const className = String(item?.name || '').trim();
      const dayKey = String(item?.day_of_week || '').trim().toLowerCase();
      if (!coachName || !dayMap.hasOwnProperty(dayKey)) return;
      const targetDay = dayMap[dayKey];
      const coachKey = coachName.toLowerCase();
      const currentValue = map.get(coachKey) || {
        dateSet: new Set(),
        classNameSet: new Set(),
        classByDateMap: new Map(),
      };
      if (className) currentValue.classNameSet.add(className);
      const dates = [];
      for (let i = 0; i < 45; i += 1) {
        const nextDate = new Date(start);
        nextDate.setDate(start.getDate() + i);
        if (nextDate.getDay() === targetDay) {
          dates.push(nextDate.toISOString().slice(0, 10));
        }
      }
      dates.forEach((date) => {
        currentValue.dateSet.add(date);
        if (!className) return;
        const existingClassSet = currentValue.classByDateMap.get(date) || new Set();
        existingClassSet.add(className);
        currentValue.classByDateMap.set(date, existingClassSet);
      });
      map.set(coachKey, currentValue);
    });
    return map;
  }, [schedules]);

  const cardioCoachClassMapFromBookings = useMemo(() => {
    const map = new Map();
    (Array.isArray(classBookings) ? classBookings : []).forEach((item) => {
      const coachName = String(item?.coach_name || '').trim();
      const className = String(item?.schedule_name || item?.class_name || '').trim();
      if (!coachName || !className) return;
      const coachKey = normalizeCoachText(coachName);
      const classSet = map.get(coachKey) || new Set();
      classSet.add(normalizeClassText(className));
      map.set(coachKey, classSet);
    });
    return map;
  }, [classBookings]);

  const cardioCoachPendingClassMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(classBookings) ? classBookings : []).forEach((item) => {
      if (!isOpenUpcomingManualCoachClassSlot(item)) return;
      const coachName = String(item?.coach_name || '').trim();
      const className = String(item?.schedule_name || item?.class_name || '').trim();
      if (!coachName || !className) return;
      const coachKey = normalizeCoachText(coachName);
      const classSet = map.get(coachKey) || new Set();
      classSet.add(normalizeClassText(className));
      map.set(coachKey, classSet);
    });
    return map;
  }, [classBookings]);

  const cardioCoachOptions = useMemo(
    () => {
      const coachMap = new Map();
      (Array.isArray(cardioCoaches) ? cardioCoaches : [])
        .filter((item) => item && item.is_active !== false)
        .forEach((item) => {
          const name = String(item.name || '').trim();
          if (!name) return;
          coachMap.set(normalizeCoachText(name), {
            name,
            sort_order: Number(item.sort_order || 0),
            sourceOrder: 0,
          });
        });

      (Array.isArray(schedules) ? schedules : []).forEach((item) => {
        const name = String(item?.instructor || '').trim();
        const key = normalizeCoachText(name);
        if (!key || coachMap.has(key)) return;
        coachMap.set(key, { name, sort_order: 9999, sourceOrder: 1 });
      });

      (Array.isArray(classBookings) ? classBookings : []).forEach((item) => {
        const name = String(item?.coach_name || '').trim();
        const key = normalizeCoachText(name);
        if (!key || coachMap.has(key)) return;
        coachMap.set(key, { name, sort_order: 9999, sourceOrder: 2 });
      });

      return [...coachMap.values()]
        .sort((a, b) => {
          if (a.sourceOrder !== b.sourceOrder) return a.sourceOrder - b.sourceOrder;
          if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
          return String(a.name || '').localeCompare(String(b.name || ''));
        })
        .map((item) => item.name);
    },
    [cardioCoaches, schedules, classBookings]
  );

  const filteredCardioPaymentTypeOptions = useMemo(() => {
    const selectedCoach = String(cardioSelection.coachName || '').trim();
    if (!selectedCoach) return [];
    const pendingClassSet = new Set();
    (Array.isArray(classBookings) ? classBookings : []).forEach((item) => {
      if (!isOpenUpcomingManualCoachClassSlot(item)) return;
      if (!isCoachNameMatch(selectedCoach, item?.coach_name)) return;
      const normalizedClassName = normalizeClassText(item?.schedule_name || item?.class_name || '');
      if (normalizedClassName) pendingClassSet.add(normalizedClassName);
    });
    return cardioPaymentTypeOptions.filter((item) => {
      const planCoachName = String(item.coach_name || '').trim();
      const packageName = normalizeClassText(item.package_name || item.label || '');
      if (pendingClassSet.size === 0) return false;
      if (planCoachName && !isCoachNameMatch(selectedCoach, planCoachName)) return false;
      return packageName ? pendingClassSet.has(packageName) : false;
    });
  }, [cardioSelection.coachName, classBookings, cardioPaymentTypeOptions]);

  const selectedCardioPackage = useMemo(
    () => cardioPaymentTypeOptions.find((item) => item.value === formData.payment_type) || null,
    [cardioPaymentTypeOptions, formData.payment_type]
  );

  const cardioDateOptions = useMemo(() => {
    const selectedCoach = String(cardioSelection.coachName || '').trim();
    if (!selectedCoach) return [];
    const selectedPackageName = normalizeClassText(selectedCardioPackage?.package_name || selectedCardioPackage?.label || '');
    const matchedDateSet = new Set();
    const coachDateSet = new Set();
    (Array.isArray(classBookings) ? classBookings : []).forEach((item) => {
      if (!isOpenUpcomingManualCoachClassSlot(item)) return;
      const bookingCoachName = String(item?.coach_name || '').trim();
      if (!isCoachNameMatch(selectedCoach, bookingCoachName)) return;
      const bookingClassName = normalizeClassText(item?.schedule_name || item?.class_name || '');
      const dateValue = extractDateValue(item?.class_date || item?.created_at);
      if (!dateValue) return;
      coachDateSet.add(dateValue);
      if (selectedPackageName && bookingClassName && bookingClassName !== selectedPackageName) return;
      matchedDateSet.add(dateValue);
    });
    const resultSet = matchedDateSet.size > 0 ? matchedDateSet : coachDateSet;
    return [...resultSet].sort((a, b) => String(a).localeCompare(String(b)));
  }, [cardioSelection.coachName, classBookings, selectedCardioPackage]);

  const getPaymentCategoryByType = (value) => {
    if (String(value || '').startsWith('plan:')) return 'plan';
    if (String(value || '').startsWith('cardio:')) return 'cardio';
    if (String(value || '').startsWith('pt:')) return 'pt';
    if (String(value || '').startsWith('all_access:')) return 'all_access';
    return '';
  };

  const derivedPaymentCategory = useMemo(
    () => {
      const resolvedCategory = getPaymentCategoryByType(formData.payment_type);
      return resolvedCategory;
    },
    [formData.payment_type]
  );
  const paymentCategory = selectedPaymentCategory || derivedPaymentCategory;
  const showMemberContactFields = Boolean(paymentCategory);

  const buildPtDescription = (ptName, selectedPackage) => {
    if (!selectedPackage) return '';
    const packageName = String(selectedPackage.package_name || selectedPackage.label || '').trim();
    const sessions = Number(selectedPackage.sessions || 0);
    const unitLabel = formatPlanIntervalUnit(selectedPackage.interval_unit || 'DAY', { pt: true }).toLowerCase();
    const ptLabel = String(ptName || 'PT').trim() || 'PT';
    const sessionsLabel = sessions > 0 ? ` (${sessions} ${unitLabel})` : '';
    return `PT Package - ${ptLabel} ${packageName}${sessionsLabel}`.replace(/\s+/g, ' ').trim();
  };

  const buildCardioDescription = (coachName, classDate, selectedPackage) => {
    if (!selectedPackage) return '';
    const packageName = String(selectedPackage.package_name || selectedPackage.label || '').trim();
    const coachLabel = String(coachName || '').trim();
    const dateLabel = String(classDate || '').trim();
    const suffixCoach = coachLabel ? ` - Coach ${coachLabel}` : '';
    const suffixDate = dateLabel ? ` - Tanggal ${dateLabel}` : '';
    const suffix = `${suffixCoach}${suffixDate}`;
    return `Cardio ${packageName}${suffix}`.replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    if (paymentCategory !== 'pt' || ptPaymentTypeOptions.length === 0) return;

    const selectedPtPackage = ptPaymentTypeOptions.find((item) => item.value === formData.payment_type);
    if (selectedPtPackage) {
      const selectedPtName = String(ptSelection.ptName || ptNameOptions[0] || '');
      setPtSelection((prev) => {
        if (prev.ptName === selectedPtName) return prev;
        return { ptName: selectedPtName };
      });
      return;
    }

    const firstPtPackage = ptPaymentTypeOptions[0];
    setPtSelection({ ptName: String(ptNameOptions[0] || '') });
    handlePaymentTypeChange(firstPtPackage.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCategory, ptPaymentTypeOptions, ptNameOptions, formData.payment_type]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, membersRes, plansRes, ptTrainersRes, schedulesRes, classBookingsRes, cardioCoachesRes, paymentMethodsRes] = await Promise.allSettled([
        axios.get(`${API}/payments`, { withCredentials: true }),
        axios.get(`${API}/members`, { withCredentials: true }),
        axios.get(`${API}/plans`, { withCredentials: true }),
        axios.get(`${API}/pt-trainers`, { withCredentials: true }),
        axios.get(`${API}/schedules`, { withCredentials: true }),
        axios.get(`${API}/class-bookings`, { withCredentials: true }),
        axios.get(`${API}/cardio-coaches`, { withCredentials: true }),
        axios.get(`${API}/payments/method-options`, { withCredentials: true }),
      ]);

      if (paymentsRes.status !== 'fulfilled' || membersRes.status !== 'fulfilled' || plansRes.status !== 'fulfilled') {
        throw new Error('Gagal mengambil data utama payments.');
      }

      setPayments(Array.isArray(paymentsRes.value?.data) ? paymentsRes.value.data : []);
      setMembers(Array.isArray(membersRes.value?.data) ? membersRes.value.data : []);
      setPlans(Array.isArray(plansRes.value?.data) ? plansRes.value.data : []);
      setPtTrainers(
        ptTrainersRes.status === 'fulfilled' && Array.isArray(ptTrainersRes.value?.data)
          ? ptTrainersRes.value.data
          : []
      );
      setSchedules(
        schedulesRes.status === 'fulfilled' && Array.isArray(schedulesRes.value?.data)
          ? schedulesRes.value.data
          : []
      );
      setClassBookings(
        classBookingsRes.status === 'fulfilled' && Array.isArray(classBookingsRes.value?.data)
          ? classBookingsRes.value.data
          : []
      );
      setCardioCoaches(
        cardioCoachesRes.status === 'fulfilled' && Array.isArray(cardioCoachesRes.value?.data)
          ? cardioCoachesRes.value.data
          : []
      );
      if (paymentMethodsRes.status === 'fulfilled' && Array.isArray(paymentMethodsRes.value?.data?.options)) {
        const normalizedOptions = paymentMethodsRes.value.data.options
          .map((item) => ({
            value: String(item?.value || '').trim(),
            label: String(item?.label || '').trim(),
          }))
          .filter((item) => item.value && item.label);
        setPaymentMethodOptions(normalizedOptions.length > 0 ? normalizedOptions : [{ value: 'Cash', label: 'Bayar Langsung' }]);
      } else {
        setPaymentMethodOptions(defaultPaymentMethodOptions);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Array.isArray(paymentMethodOptions) || paymentMethodOptions.length === 0) return;
    if (paymentMethodOptions.some((item) => item.value === formData.payment_method)) return;
    setFormData((prev) => ({ ...prev, payment_method: paymentMethodOptions[0].value }));
  }, [paymentMethodOptions, formData.payment_method]);

  const handlePaymentTypeChange = (value) => {
    const nextCategory = getPaymentCategoryByType(value);
    if (nextCategory) setSelectedPaymentCategory(nextCategory);
    const selectedType = [
      ...membershipPaymentTypeOptions,
      ...cardioPaymentTypeOptions,
      ...ptPaymentTypeOptions,
      ...allAccessPaymentTypeOptions,
    ].find((t) => t.value === value);
    if (selectedType && selectedType.price !== null) {
      const resolvedPrice = Number(selectedType.price || 0);
      const isPtPackage = String(value || '').startsWith('pt:');
      const isCardioPackage = String(value || '').startsWith('cardio:');
      const ptDescription = isPtPackage ? buildPtDescription(ptSelection.ptName, selectedType) : null;
      const cardioDescription = isCardioPackage
        ? buildCardioDescription(
            cardioSelection.coachName,
            cardioSelection.classDate,
            selectedType
          )
        : null;
      setFormData((prev) => ({
        ...prev,
        payment_type: value,
        visitor_phone: prev.visitor_phone,
        visitor_address: prev.visitor_address,
        amount: String(resolvedPrice),
        description: ptDescription || cardioDescription || selectedType.description || selectedType.label,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      payment_type: value,
      visitor_phone: prev.visitor_phone,
      visitor_address: prev.visitor_address,
      amount: '',
      description: '',
    }));
  };

  const handlePaymentCategoryChange = (value) => {
    setSelectedPaymentCategory(value);
    if (value === 'plan') {
      if (membershipPaymentTypeOptions.length > 0) {
        handlePaymentTypeChange(membershipPaymentTypeOptions[0].value);
      } else {
        alert('Belum ada paket gym aktif di menu Plans.');
      setFormData((prev) => ({
        ...prev,
          payment_type: '',
          amount: '',
          description: '',
        }));
      }
      return;
    }
    if (value === 'cardio') {
      setCardioSelection({ coachName: '', classDate: '' });
      if (cardioPaymentTypeOptions.length === 0) {
        alert('Belum ada paket cardio aktif di menu Plans kategori CARDIO.');
      }
      setFormData((prev) => ({
        ...prev,
        payment_type: '',
        amount: '',
        description: '',
      }));
      return;
    }
    if (value === 'pt') {
      if (ptPaymentTypeOptions.length > 0 && ptNameOptions.length > 0) {
        const firstPtPackage = ptPaymentTypeOptions[0];
        setPtSelection({ ptName: String(ptNameOptions[0] || '') });
        handlePaymentTypeChange(firstPtPackage.value);
      } else {
        alert('Paket PT atau nama PT belum ada. Lengkapi dulu di menu Plans (kategori PT) dan PT Menu.');
        setFormData((prev) => ({
          ...prev,
          payment_type: '',
          amount: '',
          description: '',
        }));
      }
      return;
    }
    if (value === 'all_access') {
      if (allAccessPaymentTypeOptions.length > 0) {
        handlePaymentTypeChange(allAccessPaymentTypeOptions[0].value);
      } else {
        alert('Belum ada paket All Access aktif di menu Plans kategori ALL ACCESS.');
        setFormData((prev) => ({
          ...prev,
          payment_type: '',
          amount: '',
          description: '',
        }));
      }
    }
  };

  useEffect(() => {
    if (!String(formData.payment_type || '').startsWith('pt:')) return;
    const selectedPtPackage = ptPaymentTypeOptions.find((item) => item.value === formData.payment_type);
    if (!selectedPtPackage) return;
    const nextDescription = buildPtDescription(ptSelection.ptName, selectedPtPackage);
    setFormData((prev) => {
      if (String(prev.description || '') === nextDescription) return prev;
      return { ...prev, description: nextDescription };
    });
  }, [formData.payment_type, ptSelection.ptName, ptPaymentTypeOptions]);

  useEffect(() => {
    if (!String(formData.payment_type || '').startsWith('cardio:')) return;
    const selectedCardioPackage = cardioPaymentTypeOptions.find((item) => item.value === formData.payment_type);
    if (!selectedCardioPackage) return;
    const nextDescription = buildCardioDescription(
      cardioSelection.coachName,
      cardioSelection.classDate,
      selectedCardioPackage
    );
    setFormData((prev) => {
      if (String(prev.description || '') === nextDescription) return prev;
      return { ...prev, description: nextDescription };
    });
  }, [
    formData.payment_type,
    cardioSelection.coachName,
    cardioSelection.classDate,
    cardioPaymentTypeOptions,
  ]);

  useEffect(() => {
    if (!String(formData.payment_type || '').startsWith('all_access:')) return;
    const selectedAllAccessPackage = allAccessPaymentTypeOptions.find((item) => item.value === formData.payment_type);
    if (!selectedAllAccessPackage) return;
    setFormData((prev) => {
      if (String(prev.description || '') === selectedAllAccessPackage.description) return prev;
      return { ...prev, description: selectedAllAccessPackage.description };
    });
  }, [formData.payment_type, allAccessPaymentTypeOptions]);

  useEffect(() => {
    if (paymentCategory !== 'cardio') return;
    if (!cardioSelection.coachName) return;
    const selectedCardioType = String(formData.payment_type || '');
    if (filteredCardioPaymentTypeOptions.some((item) => item.value === selectedCardioType)) return;
    const fallbackPackage = filteredCardioPaymentTypeOptions[0];
    if (fallbackPackage) {
      handlePaymentTypeChange(fallbackPackage.value);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      payment_type: '',
      amount: '',
      description: '',
      preferred_start_date: new Date().toISOString().slice(0, 10),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentCategory, cardioSelection.coachName, filteredCardioPaymentTypeOptions, formData.payment_type]);

  useEffect(() => {
    if (!String(formData.payment_type || '').startsWith('cardio:')) return;
    if (!cardioSelection.classDate) return;
    setFormData((prev) => {
      if (prev.preferred_start_date === cardioSelection.classDate) return prev;
      return { ...prev, preferred_start_date: cardioSelection.classDate };
    });
  }, [formData.payment_type, cardioSelection.classDate]);

  useEffect(() => {
    if (paymentCategory !== 'cardio') return;
    if (!cardioSelection.coachName) return;
    if (cardioDateOptions.length === 0) return;
    const autoDate = pickAutoClassDate(cardioDateOptions);
    if (!autoDate) return;
    if (cardioSelection.classDate && cardioDateOptions.includes(cardioSelection.classDate)) return;
    setCardioSelection((prev) => ({ ...prev, classDate: autoDate }));
  }, [paymentCategory, cardioSelection.coachName, cardioSelection.classDate, cardioDateOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentCategory) {
      alert('Pilih kategori terlebih dulu.');
      return;
    }
    if (paymentCategory === 'cardio' && !String(formData.payment_type || '').startsWith('cardio:')) {
      alert('Pilih kelas cardio yang sudah ditambahkan di Member Kelas coach terlebih dulu.');
      return;
    }
    if (String(formData.payment_type || '').startsWith('cardio:') && !String(cardioSelection.coachName || '').trim()) {
      alert('Coach cardio wajib dipilih dari jadwal yang tersedia.');
      return;
    }
    if (String(formData.payment_type || '').startsWith('cardio:') && !String(cardioSelection.classDate || '').trim()) {
      alert('Tanggal kelas cardio wajib dipilih.');
      return;
    }
    const selectedCardioPackage = String(formData.payment_type || '').startsWith('cardio:')
      ? cardioPaymentTypeOptions.find((item) => item.value === formData.payment_type)
      : null;
    const selectedCardioClassName = selectedCardioPackage
      ? String(selectedCardioPackage.package_name || selectedCardioPackage.label || '').trim()
      : '';
    const resolvedMemberId = formData.member_id;
    const normalizedMemberName = String(memberQuery || '').trim();
    if (!resolvedMemberId && !normalizedMemberName) {
      alert('Nama member wajib diisi.');
      return;
    }

    const executePayment = async () => {
      try {
        const { data: createdPayment } = await axios.post(
          `${API}/payments`,
          {
            member_id: resolvedMemberId || null,
            visitor_name: resolvedMemberId ? null : normalizedMemberName,
            visitor_phone: String(formData.visitor_phone || '').trim(),
            visitor_address: String(formData.visitor_address || '').trim(),
            amount: parseFloat(formData.amount),
            description: formData.description,
            payment_method: formData.payment_method,
            payment_type: formData.payment_type,
            class_name: selectedCardioClassName || null,
            preferred_start_date: formData.preferred_start_date || null,
          },
          { withCredentials: true }
        );
        toast.success('Payment berhasil disimpan', {
          description: `${createdPayment?.member_name || normalizedMemberName} - ${formatRupiah(createdPayment?.amount || formData.amount)}`,
        });
        setIsModalOpen(false);
        setFormData({
          member_id: '',
          visitor_phone: '',
          visitor_address: '',
          amount: '',
          description: '',
          payment_type: '',
          payment_method: 'Cash',
          preferred_start_date: new Date().toISOString().slice(0, 10),
        });
        setCardioSelection({ coachName: '', classDate: '' });
        setSelectedPaymentCategory('');
        setMemberQuery('');
        setShowMemberDropdown(false);
        setDuplicateDialogOpen(false);
        setDuplicateNames([]);
        setPendingSubmit(null);
        fetchData();
      } catch (error) {
        console.error('Failed to create payment:', error);
        alert(error.response?.data?.detail || 'Failed to create payment');
      }
    };

    const phoneValue = String(formData.visitor_phone || '').trim();
    if (phoneValue) {
      try {
        const checkRes = await axios.get(`${API}/payments/check-duplicate`, {
          params: { phone: phoneValue },
          withCredentials: true,
        });
        if (checkRes.data?.has_duplicate) {
          setDuplicateNames(checkRes.data.duplicate_names || []);
          setDuplicateDialogOpen(true);
          setPendingSubmit({ execute: executePayment });
          return;
        }
      } catch (checkError) {
        console.error('Failed to check duplicate:', checkError);
      }
    }

    await executePayment();
  };

  const totalRevenue = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const filteredMembers = members
    .filter((member) => String(member.name || '').toLowerCase().includes(memberQuery.trim().toLowerCase()))
    .slice(0, 8);
  const thisMonthRevenue = payments
    .filter((p) => {
      const paymentDate = new Date(p.created_at);
      const now = new Date();
      return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  return (
    <DashboardLayout>
      <Header title="Paket BF" />
      <div className="space-y-7 p-8" data-testid="payments-page">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F3D77A]">
              <ReceiptText className="h-3.5 w-3.5" />
              Paket BF
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Riwayat Pembayaran Member</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Catat pembayaran Gym, Cardio, PT, dan All Access dalam satu tempat.
            </p>
          </div>
          <Button
            onClick={() => {
              setIsModalOpen(true);
              setSelectedPaymentCategory('');
              setFormData({
                member_id: '',
                visitor_phone: '',
                visitor_address: '',
                amount: '',
                description: '',
                payment_type: '',
                payment_method: 'Cash',
                preferred_start_date: new Date().toISOString().slice(0, 10),
              });
              setCardioSelection({ coachName: '', classDate: '' });
              setMemberQuery('');
              setShowMemberDropdown(false);
            }}
            className="h-11 rounded-sm bg-[#D4AF37] px-5 font-semibold text-black hover:bg-[#C59A27]"
          >
            <Plus className="mr-2 h-4 w-4" />
            Catat Pembayaran
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.035),rgba(9,9,11,0.88))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Revenue</p>
              <WalletCards className="h-5 w-5 text-zinc-600" />
            </div>
            <p className="text-3xl font-black tracking-tighter text-white">{formatRupiah(totalRevenue)}</p>
            <p className="mt-2 text-xs text-zinc-500">Akumulasi seluruh pembayaran tercatat.</p>
          </div>
          <div className="rounded-sm border border-[#D4AF37]/25 bg-[linear-gradient(135deg,rgba(212,175,55,0.14),rgba(9,9,11,0.88))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Bulan Ini</p>
              <CalendarDays className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-black tracking-tighter text-white">{formatRupiah(thisMonthRevenue)}</p>
            <p className="mt-2 text-xs text-zinc-500">Pendapatan yang masuk bulan berjalan.</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.035),rgba(9,9,11,0.88))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Transaksi</p>
              <CreditCard className="h-5 w-5 text-zinc-600" />
            </div>
            <p className="text-3xl font-black tracking-tighter text-white">{payments.length}</p>
            <p className="mt-2 text-xs text-zinc-500">Jumlah transaksi pembayaran member.</p>
          </div>
        </div>
        <div className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/45 shadow-[0_20px_80px_rgba(0,0,0,0.18)]">
          <div className="flex flex-col justify-between gap-2 border-b border-white/10 px-5 py-4 md:flex-row md:items-center">
            <div>
              <h3 className="text-base font-semibold text-white">Daftar Transaksi</h3>
              <p className="mt-1 text-xs text-zinc-500">Menampilkan pembayaran terbaru yang sudah tercatat.</p>
            </div>
            <Badge className="w-fit border-white/10 bg-white/5 text-zinc-300">
              {payments.length} transaksi
            </Badge>
          </div>
          <div className="max-h-[58vh] overflow-auto">
          <Table>
            <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="min-w-[190px]">No. Transaksi</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead className="min-w-[320px]">Paket</TableHead>
                  <TableHead>Metode</TableHead>
                  <TableHead>Jumlah</TableHead>
                  <TableHead className="min-w-[170px]">Tanggal</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : payments.length > 0 ? (
                payments.map((payment) => {
                  const categoryBadge = getPaymentCategoryBadge(payment.description);
                  return (
                  <TableRow key={payment.id} className="border-white/5 transition-colors hover:bg-white/[0.025]">
                    <TableCell>
                      <p className="font-mono text-xs font-semibold text-zinc-200">{payment.transaction_number || '-'}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-white">{payment.member_name}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5">
                        <Badge className={`w-fit ${categoryBadge.className}`}>{categoryBadge.label}</Badge>
                        <p className="font-medium text-zinc-200">{payment.description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="rounded-sm bg-white/[0.04] px-2 py-1 text-xs font-medium text-zinc-300">
                        {paymentMethodLabelMap[payment.payment_method] || payment.payment_method}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold text-white">{formatRupiah(payment.amount)}</TableCell>
                    <TableCell>
                      <p className="text-sm text-zinc-300">{format(new Date(payment.created_at), 'dd MMM yyyy')}</p>
                      <p className="text-xs text-zinc-500">{format(new Date(payment.created_at), 'HH:mm')}</p>
                    </TableCell>
                    <TableCell>
                      <Badge className={getPaymentStatusBadgeClass(payment.status)}>
                        {payment.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">Tidak ada pembayaran</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </div>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="flex max-h-[92vh] max-w-4xl flex-col overflow-hidden border-white/10 bg-zinc-900 p-0 text-white">
            <DialogHeader className="shrink-0 border-b border-white/10 px-6 py-5">
              <DialogTitle>Catat Pembayaran</DialogTitle>
              <p className="mt-1 text-sm text-zinc-500">
                Isi data member, pilih kategori paket, lalu simpan pembayaran.
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative space-y-3 rounded-sm border border-white/10 bg-black/20 p-4 md:col-span-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">1. Data Member</p>
                  <p className="mt-1 text-xs text-zinc-500">Pilih member lama atau ketik nama baru.</p>
                </div>
                <Label>Member</Label>
                <Input
                  value={memberQuery}
                  onFocus={() => setShowMemberDropdown(true)}
                  onBlur={() => {
                    window.setTimeout(() => setShowMemberDropdown(false), 120);
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMemberQuery(value);
                    setShowMemberDropdown(true);
                    setFormData((prev) => ({
                      ...prev,
                      member_id: '',
                      visitor_phone: prev.member_id ? '' : prev.visitor_phone,
                      visitor_address: prev.member_id ? '' : prev.visitor_address,
                    }));
                  }}
                  placeholder="Ketik nama member"
                />
                {showMemberDropdown && memberQuery.trim() && (
                  <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-sm border border-white/10 bg-zinc-900 shadow-lg">
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member) => (
                        <button
                          key={member.id}
                          type="button"
                          className="block w-full border-b border-white/5 px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setMemberQuery(member.name);
                            setFormData((prev) => ({
                              ...prev,
                              member_id: member.id,
                              visitor_phone: member.phone || '',
                              visitor_address: member.address || '',
                            }));
                            setShowMemberDropdown(false);
                          }}
                        >
                          <span className="font-medium">{member.name}</span>
                          <span className="ml-2 text-xs text-zinc-500">
                            {member.phone ? `• ${member.phone}` : '• Tanpa No. HP'}
                          </span>
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-zinc-500">Member tidak ditemukan</p>
                    )}
                  </div>
                )}
                {!formData.member_id && memberQuery.trim() && (
                  <p className="text-[11px] text-amber-400">
                    Jika nama belum terdaftar, sistem akan otomatis membuat member baru saat pembayaran disimpan.
                  </p>
                )}
              </div>
              {showMemberContactFields && (
                <div className="rounded-sm border border-white/10 bg-black/20 p-4 md:col-span-2">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Kontak Member</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>No HP</Label>
                    <Input
                      value={formData.visitor_phone}
                      onChange={(e) => setFormData((prev) => ({ ...prev, visitor_phone: e.target.value }))}
                      placeholder="Contoh: 08123456789"
                    />
                    <p className="text-[11px] text-zinc-500">
                      Akan disimpan ke data member.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Alamat Lengkap Tempat Tinggal</Label>
                    <Textarea
                      value={formData.visitor_address}
                      onChange={(e) => setFormData((prev) => ({ ...prev, visitor_address: e.target.value }))}
                      placeholder="Contoh: Jl. Sudirman No. 12, RT/RW, Kelurahan, Kecamatan, Kota"
                      className="min-h-[92px] resize-none"
                    />
                  </div>
                </div>
                </div>
              )}
              <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4">
                <Label>Kategori</Label>
                <Select value={paymentCategory || undefined} onValueChange={handlePaymentCategoryChange}>
                  <SelectTrigger><SelectValue placeholder="Pilih Kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plan">Gym</SelectItem>
                    <SelectItem value="cardio">Cardio</SelectItem>
                    <SelectItem value="pt">Paket PT</SelectItem>
                    <SelectItem value="all_access">All Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {paymentCategory === 'plan' && (
                <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4">
                  <Label>Paket Gym</Label>
                  <Select
                    value={
                      String(formData.payment_type || '').startsWith('plan:')
                        ? formData.payment_type
                        : (membershipPaymentTypeOptions[0]?.value || undefined)
                    }
                    onValueChange={handlePaymentTypeChange}
                  >
                    <SelectTrigger><SelectValue placeholder="Pilih paket gym" /></SelectTrigger>
                    <SelectContent>
                      {membershipPaymentTypeOptions.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {membershipPaymentTypeOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Belum ada paket gym aktif di menu Plans.
                    </p>
                  )}
                </div>
              )}
              {paymentCategory === 'cardio' && (
                <div className="space-y-3 rounded-sm border border-white/10 bg-black/20 p-4 md:col-span-2">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Paket Cardio</Label>
                      <Select
                        value={
                          String(formData.payment_type || '').startsWith('cardio:')
                            && filteredCardioPaymentTypeOptions.some((item) => item.value === formData.payment_type)
                            ? formData.payment_type
                            : undefined
                        }
                        onValueChange={handlePaymentTypeChange}
                        disabled={!cardioSelection.coachName || filteredCardioPaymentTypeOptions.length === 0}
                      >
                        <SelectTrigger><SelectValue placeholder={cardioSelection.coachName ? "Pilih paket cardio" : "Pilih coach dulu"} /></SelectTrigger>
                        <SelectContent>
                          {filteredCardioPaymentTypeOptions.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Coach</Label>
                      <Select
                        value={cardioSelection.coachName}
                        onValueChange={(value) =>
                          setCardioSelection({
                            coachName: String(value || ''),
                            classDate: '',
                          })
                        }
                      >
                        <SelectTrigger><SelectValue placeholder="Pilih coach" /></SelectTrigger>
                        <SelectContent>
                          {cardioCoachOptions.map((coachName) => (
                            <SelectItem key={coachName} value={coachName}>{coachName}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tanggal Kelas</Label>
                      <Input
                        type="date"
                        value={cardioSelection.classDate || ''}
                        onChange={(e) =>
                          setCardioSelection((prev) => ({
                            ...prev,
                            classDate: String(e.target.value || ''),
                          }))
                        }
                        disabled={!cardioSelection.coachName}
                      />
                    </div>
                  </div>
                  {cardioPaymentTypeOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Belum ada paket cardio aktif di menu Plans kategori CARDIO.
                    </p>
                  )}
                  {cardioPaymentTypeOptions.length > 0 && cardioCoachOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Belum ada jadwal coach. Tambahkan dulu di menu Class Schedules.
                    </p>
                  )}
                  {cardioSelection.coachName && filteredCardioPaymentTypeOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Tidak ada paket cardio yang belum dimulai untuk coach ini.
                    </p>
                  )}
                  {cardioSelection.coachName && filteredCardioPaymentTypeOptions.length > 0 && cardioDateOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Belum ada tanggal kelas tercatat. Silakan isi tanggal secara manual.
                    </p>
                  )}
                </div>
              )}
              {paymentCategory === 'pt' && (
                <div className="space-y-3 rounded-sm border border-white/10 bg-black/20 p-4 md:col-span-2">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Nama PT</Label>
                      <Select
                        value={ptSelection.ptName || undefined}
                        onValueChange={(ptNameValue) => {
                          setPtSelection((prev) => ({
                            ...prev,
                            ptName: String(ptNameValue || ''),
                          }));
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Pilih PT" /></SelectTrigger>
                        <SelectContent>
                          {ptNameOptions.map((ptName) => (
                            <SelectItem key={ptName} value={ptName}>{ptName}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Paket PT</Label>
                      <Select
                        value={
                          String(formData.payment_type || '').startsWith('pt:')
                            ? formData.payment_type
                            : (ptPaymentTypeOptions[0]?.value || undefined)
                        }
                        onValueChange={handlePaymentTypeChange}
                      >
                        <SelectTrigger><SelectValue placeholder="Pilih paket PT" /></SelectTrigger>
                        <SelectContent>
                          {ptPaymentTypeOptions.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label} - {formatRupiah(type.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {ptPaymentTypeOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Paket PT belum tersedia. Tambahkan dulu dari menu Plans dengan kategori PT.
                    </p>
                  )}
                  {ptNameOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Nama PT belum tersedia. Tambahkan dulu di PT Menu.
                    </p>
                  )}
                </div>
              )}
              {paymentCategory === 'all_access' && (
                <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4">
                  <Label>Paket All Access</Label>
                  <Select
                    value={
                      String(formData.payment_type || '').startsWith('all_access:')
                        ? formData.payment_type
                        : (allAccessPaymentTypeOptions[0]?.value || undefined)
                    }
                    onValueChange={handlePaymentTypeChange}
                  >
                    <SelectTrigger><SelectValue placeholder="Pilih paket all access" /></SelectTrigger>
                    <SelectContent>
                      {allAccessPaymentTypeOptions.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label} - {formatRupiah(type.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {allAccessPaymentTypeOptions.length === 0 && (
                    <p className="text-[11px] text-amber-400">
                      Belum ada paket All Access aktif di menu Plans kategori ALL ACCESS.
                    </p>
                  )}
                </div>
              )}
              {formData.payment_type.startsWith('pt:') && (
                <p className="rounded-sm border border-white/10 bg-black/20 px-3 py-2 text-xs text-zinc-500 md:col-span-2">
                  Harga paket PT mengikuti master Plans (kategori PT).
                </p>
              )}
              {formData.payment_type.startsWith('cardio:') && (
                <p className="rounded-sm border border-white/10 bg-black/20 px-3 py-2 text-xs text-zinc-500 md:col-span-2">
                  Harga paket cardio mengikuti master Plans kategori CARDIO.
                </p>
              )}
              {formData.payment_type.startsWith('all_access:') && (
                <p className="rounded-sm border border-white/10 bg-black/20 px-3 py-2 text-xs text-zinc-500 md:col-span-2">
                  All Access memberi akses ke GYM, CARDIO, dan PT selama masa aktif paket.
                </p>
              )}
              {(formData.payment_type.startsWith('plan:') || formData.payment_type.startsWith('all_access:')) && (
                <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4">
                  <Label>{formData.payment_type.startsWith('all_access:') ? 'Tanggal Mulai All Access' : 'Tanggal Mulai Plan'}</Label>
                  <Input
                    type="date"
                    value={formData.preferred_start_date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, preferred_start_date: e.target.value }))}
                    required
                  />
                </div>
              )}
              <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4">
                <Label>Metode Pembayaran</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData((prev) => ({ ...prev, payment_method: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentMethodOptions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4">
                <Label>Jumlah (Rp)</Label>
                <Input type="number" value={formData.amount} onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))} required disabled={formData.payment_type !== 'custom'} />
              </div>
              <div className="space-y-2 rounded-sm border border-white/10 bg-black/20 p-4 md:col-span-2">
                <Label>Deskripsi</Label>
                <Input value={formData.description} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} required disabled={formData.payment_type !== 'custom'} />
              </div>
                </div>
              </div>
              <div className="shrink-0 border-t border-white/10 bg-zinc-900 px-6 py-4">
              <Button
                type="submit"
                className="h-11 w-full rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                disabled={!formData.amount || !formData.description}
              >
                Catat Pembayaran
              </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={duplicateDialogOpen} onOpenChange={(open) => { setDuplicateDialogOpen(open); if (!open) { setPendingSubmit(null); setDuplicateNames([]); } }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Konfirmasi Duplikat</DialogTitle>
              <DialogDescription className="text-zinc-400">
                No HP yang sama sudah ada di transaksi hari ini
                {duplicateNames.length > 0 && (
                  <span className="block mt-2 font-medium text-zinc-300">
                    {duplicateNames.join(', ')}
                  </span>
                )}
                . Apakah tetap ingin melanjutkan?
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => { setDuplicateDialogOpen(false); setPendingSubmit(null); setDuplicateNames([]); }}
                className="rounded-sm border-white/10 text-white hover:bg-zinc-800"
              >
                Batal
              </Button>
              <Button
                onClick={() => { if (pendingSubmit?.execute) pendingSubmit.execute(); }}
                className="rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
              >
                Lanjutkan
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
